"use strict";
// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
Object.defineProperty(exports, "__esModule", { value: true });
exports.selectAadAppManifestQuestionNode = exports.envQuestionCondition = exports.listCollaboratorQuestionNode = exports.grantPermissionQuestionNode = void 0;
const tslib_1 = require("tslib");
const teamsfx_api_1 = require("@microsoft/teamsfx-api");
const path = tslib_1.__importStar(require("path"));
const featureFlags_1 = require("../common/featureFlags");
const localizeUtils_1 = require("../common/localizeUtils");
const collaborator_1 = require("../core/collaborator");
const constants_1 = require("./constants");
const other_1 = require("./other");
function grantPermissionQuestionNode() {
    var _a, _b;
    const selectAppManifestNode = (0, other_1.selectTeamsAppManifestQuestionNode)();
    selectAppManifestNode.condition = {
        contains: collaborator_1.CollaborationConstants.TeamsAppQuestionId,
    };
    (_a = selectAppManifestNode.children) === null || _a === void 0 ? void 0 : _a.push({
        condition: envQuestionCondition,
        data: (0, other_1.selectTargetEnvQuestion)(constants_1.QuestionNames.Env, false, false, ""),
    });
    const selectAadAppNode = selectAadAppManifestQuestionNode();
    selectAadAppNode.condition = { contains: collaborator_1.CollaborationConstants.AadAppQuestionId };
    (_b = selectAadAppNode.children) === null || _b === void 0 ? void 0 : _b.push({
        condition: envQuestionCondition,
        data: (0, other_1.selectTargetEnvQuestion)(constants_1.QuestionNames.Env, false, false, ""),
    });
    return {
        data: { type: "group" },
        children: [
            {
                condition: (inputs) => teamsfx_api_1.DynamicPlatforms.includes(inputs.platform),
                data: selectAppTypeQuestion(),
                cliOptionDisabled: "self",
                inputsDisabled: "self",
                children: [
                    selectAppManifestNode,
                    selectAadAppNode,
                    {
                        data: (0, other_1.inputUserEmailQuestion)((0, localizeUtils_1.getLocalizedString)("core.getUserEmailQuestion.title"), "Email address of the collaborator.", true),
                    },
                ],
            },
        ],
    };
}
exports.grantPermissionQuestionNode = grantPermissionQuestionNode;
function listCollaboratorQuestionNode() {
    var _a, _b;
    const selectAppManifestNode = (0, other_1.selectTeamsAppManifestQuestionNode)();
    selectAppManifestNode.condition = {
        contains: collaborator_1.CollaborationConstants.TeamsAppQuestionId,
    };
    (_a = selectAppManifestNode.children) === null || _a === void 0 ? void 0 : _a.push({
        condition: envQuestionCondition,
        data: (0, other_1.selectTargetEnvQuestion)(constants_1.QuestionNames.Env, false, false, ""),
    });
    const selectAadAppNode = selectAadAppManifestQuestionNode();
    selectAadAppNode.condition = { contains: collaborator_1.CollaborationConstants.AadAppQuestionId };
    (_b = selectAadAppNode.children) === null || _b === void 0 ? void 0 : _b.push({
        condition: envQuestionCondition,
        data: (0, other_1.selectTargetEnvQuestion)(constants_1.QuestionNames.Env, false, false, ""),
    });
    return {
        data: { type: "group" },
        children: [
            {
                condition: (inputs) => teamsfx_api_1.DynamicPlatforms.includes(inputs.platform),
                data: selectAppTypeQuestion(),
                cliOptionDisabled: "self",
                inputsDisabled: "self",
                children: [selectAppManifestNode, selectAadAppNode],
            },
        ],
    };
}
exports.listCollaboratorQuestionNode = listCollaboratorQuestionNode;
async function envQuestionCondition(inputs) {
    const appType = inputs[collaborator_1.CollaborationConstants.AppType];
    const requireAad = appType === null || appType === void 0 ? void 0 : appType.includes(collaborator_1.CollaborationConstants.AadAppQuestionId);
    const requireTeams = appType === null || appType === void 0 ? void 0 : appType.includes(collaborator_1.CollaborationConstants.TeamsAppQuestionId);
    const aadManifestPath = inputs[constants_1.QuestionNames.AadAppManifestFilePath];
    const teamsManifestPath = inputs[constants_1.QuestionNames.TeamsAppManifestFilePath];
    // When both is selected, only show the question once at the end
    if ((requireAad && !aadManifestPath) || (requireTeams && !teamsManifestPath)) {
        return false;
    }
    // Only show env question when manifest id is referencing value from .env file
    let requireEnv = false;
    if (requireTeams && teamsManifestPath) {
        const teamsAppIdRes = await collaborator_1.CollaborationUtil.loadManifestId(teamsManifestPath);
        if (teamsAppIdRes.isOk()) {
            requireEnv = collaborator_1.CollaborationUtil.requireEnvQuestion(teamsAppIdRes.value);
            if (requireEnv) {
                return true;
            }
        }
        else {
            return false;
        }
    }
    if (requireAad && aadManifestPath) {
        const aadAppIdRes = await collaborator_1.CollaborationUtil.loadManifestId(aadManifestPath);
        if (aadAppIdRes.isOk()) {
            requireEnv = collaborator_1.CollaborationUtil.requireEnvQuestion(aadAppIdRes.value);
            if (requireEnv) {
                return true;
            }
        }
        else {
            return false;
        }
    }
    return false;
}
exports.envQuestionCondition = envQuestionCondition;
function selectAadAppManifestQuestionNode() {
    return {
        data: (0, other_1.selectAadManifestQuestion)(),
        children: [
            {
                condition: (inputs) => inputs.platform === teamsfx_api_1.Platform.VSCode && // confirm question only works for VSC
                    inputs.projectPath &&
                    inputs[constants_1.QuestionNames.AadAppManifestFilePath] &&
                    path.resolve(inputs[constants_1.QuestionNames.AadAppManifestFilePath]) !==
                        path.join(inputs.projectPath, "aad.manifest.json"),
                data: (0, other_1.confirmManifestQuestion)(false, false),
                cliOptionDisabled: "self",
                inputsDisabled: "self",
            },
        ],
    };
}
exports.selectAadAppManifestQuestionNode = selectAadAppManifestQuestionNode;
function selectAppTypeQuestion() {
    const options = {
        name: constants_1.QuestionNames.collaborationAppType,
        title: (0, localizeUtils_1.getLocalizedString)("core.selectCollaborationAppTypeQuestion.title"),
        type: "multiSelect",
        staticOptions: [
            {
                id: collaborator_1.CollaborationConstants.AadAppQuestionId,
                label: (0, localizeUtils_1.getLocalizedString)("core.aadAppQuestion.label"),
                description: (0, localizeUtils_1.getLocalizedString)("core.aadAppQuestion.description"),
            },
            {
                id: collaborator_1.CollaborationConstants.TeamsAppQuestionId,
                label: (0, localizeUtils_1.getLocalizedString)("core.teamsAppQuestion.label"),
                description: (0, localizeUtils_1.getLocalizedString)("core.teamsAppQuestion.description"),
            },
        ],
        validation: { minItems: 1 },
        validationHelp: "Please select at least one app type.",
    };
    if (featureFlags_1.featureFlagManager.getBooleanValue(featureFlags_1.FeatureFlags.ShareEnabled)) {
        options.staticOptions.push({
            id: collaborator_1.CollaborationConstants.AgentOptionId,
            label: (0, localizeUtils_1.getLocalizedString)("core.collaboration.agent.label"),
            description: (0, localizeUtils_1.getLocalizedString)("core.collaboration.agent.description"),
        });
    }
    return options;
}
//# sourceMappingURL=collaborator.js.map